/* White Out cart celebration.
 * Waits for the theme to render the refreshed cart drawer, then plays only
 * when the product that was just added is marked as a White Out item.
 */
(function () {
  const DRAWER_SELECTOR = '[data-cart-drawer]';
  const ITEM_SELECTOR = '[data-white-out-item][data-cart-product-id]';
  const TRANSIENT_SELECTOR = '[data-white-out-effect]';
  const WAIT_TIMEOUT = 5000;
  const CELEBRATION_DEDUPE_MS = 3000;
  let rebuySmartCartVisible = false;
  let pendingCelebration = null;
  const recentCelebrations = new Map();

  function prefersReducedMotion() {
    return window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function productIdsFromItem(item) {
    const items = item && Array.isArray(item.items) ? item.items : [item];

    return new Set(
      items
        .filter(Boolean)
        .map(item => item.product_id || (item.product && item.product.id))
        .filter(Boolean)
        .map(String),
    );
  }

  function addedProductIds(detail) {
    return productIdsFromItem(detail && (detail.line_item || detail.item));
  }

  function drawerContainsAddedWhiteOutItem(drawer, productIds) {
    return Array.from(drawer.querySelectorAll(ITEM_SELECTOR)).some(item => (
      productIds.has(item.dataset.cartProductId)
    ));
  }

  function clearEffect(drawer, removeActiveState) {
    (drawer.whiteOutTimers || []).forEach(timer => window.clearTimeout(timer));
    drawer.whiteOutTimers = [];
    drawer.querySelectorAll(TRANSIENT_SELECTOR).forEach(node => node.remove());

    if (removeActiveState) {
      drawer.classList.remove('white-out-cart');
    }
  }

  function createConfetti(drawer) {
    const layer = document.createElement('div');
    const colors = ['#ffffff', '#f3f1ee', '#e6e3dc', '#c9c4b8', '#6b7280'];

    layer.className = 'white-out-cart__confetti';
    layer.dataset.whiteOutEffect = 'confetti';
    layer.setAttribute('aria-hidden', 'true');

    for (let index = 0; index < 28; index += 1) {
      const piece = document.createElement('span');
      const column = (index * 37) % 101;
      const drift = ((index * 23) % 80) - 40;

      piece.className = 'white-out-cart__confetti-piece';
      piece.style.setProperty('--white-out-x', `${column}%`);
      piece.style.setProperty('--white-out-drift', `${drift}px`);
      piece.style.setProperty('--white-out-delay', `${(index % 8) * 75}ms`);
      piece.style.setProperty('--white-out-duration', `${1250 + (index % 6) * 130}ms`);
      piece.style.setProperty('--white-out-rotation', `${180 + (index % 5) * 90}deg`);
      piece.style.setProperty('--white-out-confetti-color', colors[index % colors.length]);
      piece.style.setProperty('--white-out-width', `${4 + (index % 3) * 2}px`);
      piece.style.setProperty('--white-out-height', `${7 + (index % 4) * 2}px`);
      layer.appendChild(piece);
    }

    drawer.appendChild(layer);
  }

  function createFlash(drawer) {
    const glint = document.createElement('div');
    const flood = document.createElement('div');
    const mark = document.createElement('span');

    glint.className = 'white-out-cart__glint';
    glint.dataset.whiteOutEffect = 'glint';
    glint.setAttribute('aria-hidden', 'true');

    mark.className = 'white-out-cart__mark';
    mark.textContent = 'WHITE OUT';

    flood.className = 'white-out-cart__flood';
    flood.dataset.whiteOutEffect = 'flood';
    flood.setAttribute('aria-hidden', 'true');
    flood.appendChild(mark);

    drawer.appendChild(glint);
    drawer.appendChild(flood);

    drawer.whiteOutTimers.push(window.setTimeout(() => {
      flood.classList.add('white-out-cart__flood--leaving');
    }, 900));
  }

  function play(drawer) {
    if (!drawer) return;

    clearEffect(drawer, false);
    drawer.classList.add('white-out-cart');

    if (prefersReducedMotion()) return;

    createConfetti(drawer);
    createFlash(drawer);

    drawer.whiteOutTimers.push(window.setTimeout(() => {
      drawer
        .querySelectorAll('[data-white-out-effect="flood"], [data-white-out-effect="glint"], [data-white-out-effect="confetti"]')
        .forEach(node => node.remove());
    }, 2500));
  }

  function celebrationKey(productIds) {
    return Array.from(productIds).sort().join('|');
  }

  function wasRecentlyCelebrated(key) {
    const lastPlayed = recentCelebrations.get(key);

    return lastPlayed && Date.now() - lastPlayed < CELEBRATION_DEDUPE_MS;
  }

  function finishCelebration(target, key) {
    if (!target || wasRecentlyCelebrated(key)) return;

    recentCelebrations.set(key, Date.now());
    pendingCelebration = null;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => play(target));
    });
  }

  function scheduleCelebration(nativeDrawer, productIds) {
    const key = celebrationKey(productIds);
    const rebuyPanel = document.querySelector('#rebuy-cart .rebuy-cart__flyout');
    const rebuyEnabled = Boolean(rebuyPanel || document.querySelector('.smart-cart--enabled'));

    if (wasRecentlyCelebrated(key) || (pendingCelebration && pendingCelebration.key === key)) return;

    if (!rebuyEnabled) {
      finishCelebration(nativeDrawer, key);
      return;
    }

    if (pendingCelebration) {
      window.clearTimeout(pendingCelebration.timeout);
    }

    pendingCelebration = { key, nativeDrawer };

    if (rebuySmartCartVisible && rebuyPanel) {
      finishCelebration(rebuyPanel, key);
      return;
    }

    pendingCelebration.timeout = window.setTimeout(() => {
      const currentRebuyPanel = document.querySelector('#rebuy-cart .rebuy-cart__flyout');
      finishCelebration(currentRebuyPanel || nativeDrawer, key);
    }, 700);
  }

  function playAfterDrawerRefresh(productIds) {
    const previousDrawer = document.querySelector(DRAWER_SELECTOR);
    let observer;
    let timeout;

    const stop = () => {
      if (observer) observer.disconnect();
      window.clearTimeout(timeout);
    };

    const check = () => {
      const drawer = document.querySelector(DRAWER_SELECTOR);

      if (!drawer || drawer === previousDrawer) return false;

      stop();

      if (!drawerContainsAddedWhiteOutItem(drawer, productIds)) return true;

      scheduleCelebration(drawer, productIds);
      return true;
    };

    observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    timeout = window.setTimeout(stop, WAIT_TIMEOUT);
    check();
  }

  function checkRebuyAddition(productIds) {
    const cartUrl = window.PXUTheme
      && window.PXUTheme.routes
      && window.PXUTheme.routes.cart_url;

    if (!cartUrl || productIds.size === 0) return;

    fetch(`${cartUrl}?section_id=cart-drawer-template`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(response => {
        if (!response.ok) throw new Error(`Cart section request failed: ${response.status}`);
        return response.text();
      })
      .then(html => {
        const drawer = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(DRAWER_SELECTOR);

        if (drawer && drawerContainsAddedWhiteOutItem(drawer, productIds)) {
          scheduleCelebration(drawer, productIds);
        }
      })
      .catch(() => {
        // The native add-to-cart listener remains available as the fallback.
      });
  }

  document.addEventListener('product:add-to-cart:success', event => {
    const productIds = addedProductIds(event.detail);

    if (productIds.size > 0) {
      playAfterDrawerRefresh(productIds);
    }
  });

  document.addEventListener('rebuy:cart.add', event => {
    checkRebuyAddition(addedProductIds(event.detail));
  });

  document.addEventListener('rebuy:smartcart.show', () => {
    rebuySmartCartVisible = true;

    if (!pendingCelebration) return;

    const rebuyPanel = document.querySelector('#rebuy-cart .rebuy-cart__flyout');

    if (rebuyPanel) {
      window.clearTimeout(pendingCelebration.timeout);
      finishCelebration(rebuyPanel, pendingCelebration.key);
    }
  });

  document.addEventListener('rebuy:smartcart.hide', () => {
    rebuySmartCartVisible = false;
  });

  window.AVOWhiteOutCart = {
    play,
    reset(drawer) {
      if (drawer) clearEffect(drawer, true);
    },
  };
}());
